import { FC } from "react";
import { motion } from "framer-motion";
import { HiShieldCheck, HiSparkles, HiServer, HiDatabase, HiCheckCircle, HiCode, HiArrowRight } from "react-icons/hi";

const features = [
  {
    icon: HiShieldCheck,
    title: "Enterprise-Grade Security",
    description: "Hybrid deployment options, highest compliance standards and no model finetuning",
  },
  {
    icon: HiSparkles,
    title: "Purpose-built AI",
    description: "Latest models, finance-specific agentic framework and model orchestration layer",
  },
  {
    icon: HiServer,
    title: "Custom Deployment",
    description: "On-premise option, custom integrations and full dedicated support in implementation",
  },
  {
    icon: HiDatabase,
    title: "Single Source of Truth",
    description: "Internal or 3rd party data integrations",
  },
  {
    icon: HiCheckCircle,
    title: "Explainability & Accuracy",
    description: "Every Quantly output has a reliable raw source of information, which users can verify",
  },
  {
    icon: HiCode,
    title: "Web App or API",
    description: "Both analysts and tech teams can build GenAI use cases",
  }
];

const AIPerformance: FC = () => {
  return (
    <section className="bg-white pt-8 pb-32">
      <div className="desktop:max-w-7xl laptop:max-w-5xl tablet:max-w-2xl mobile:max-w-sm mx-auto px-6">
        <div className="grid desktop:grid-cols-3 laptop:grid-cols-2 tablet:grid-cols-2 mobile:grid-cols-1 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl p-8 text-center border border-gray-100 desktop:group laptop:group tablet:group mobile:static desktop:hover:shadow-2xl laptop:hover:shadow-2xl tablet:hover:shadow-2xl mobile:shadow-none desktop:transition-all laptop:transition-all tablet:transition-all mobile:transition-none desktop:duration-200 laptop:duration-200 tablet:duration-200 will-change-auto"
            >
              <div className="w-16 h-16 bg-black rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg desktop:group-hover:bg-gray-900 laptop:group-hover:bg-gray-900 tablet:group-hover:bg-gray-900 mobile:bg-black desktop:transition-colors laptop:transition-colors tablet:transition-colors mobile:transition-none desktop:duration-200 laptop:duration-200 tablet:duration-200 will-change-auto">
                <feature.icon className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-gray-900 text-xl font-bold mb-4">
                {feature.title}
              </h3>
              <p className="text-gray-600 text-base leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="mt-32 desktop:mx-[-6rem] laptop:mx-[-4rem] tablet:mx-[-2rem] mobile:mx-0"
        >
          <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 rounded-[2.5rem] p-1 shadow-[0_0_100px_rgba(0,0,0,0.2)] group">
            <div className="absolute inset-0 bg-gradient-to-r from-gray-800/50 via-transparent to-gray-800/50 rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative bg-gradient-to-r from-gray-900 via-black to-gray-900 rounded-[2.4rem] p-16 overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(255,255,255,0.1),_transparent_50%)]"></div>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(255,255,255,0.05),_transparent_50%)]"></div>
              <div className="relative max-w-5xl mx-auto">
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2, duration: 0.4 }}
                  className="text-center"
                >
                  <h2 className="text-white desktop:text-5xl laptop:text-4xl tablet:text-3xl mobile:text-2xl font-bold leading-tight mb-8">
                    Ensure positive ROI on your GenAI strategy
                  </h2>
                  <p className="text-gray-300 desktop:text-2xl laptop:text-xl tablet:text-lg mobile:text-base leading-relaxed mb-12 max-w-4xl mx-auto">
                    Retain full control of your GenAI tech stack and data, without building from scratch.
                  </p>
                  <a
                    href="https://form.typeform.com/to/uvfk1idp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 bg-white text-black px-8 py-4 rounded-2xl font-semibold text-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    Get Started
                    <HiArrowRight className="w-5 h-5" />
                  </a>
                </motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AIPerformance;
